'use client';

import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Search, MapPin, X } from 'lucide-react';
import { loadPlansAsync, listCountries } from '@/lib/pricingData';
import { useTranslations } from '@/lib/i18n-client';

interface Country {
  code: string;
  name: string;
}

interface DestinationSearchProps {
  /** Called when a country is selected with its ISO code */
  onSelect?: (code: string, name: string) => void;
  value?: string; // Current selection code
  placeholder?: string;
  className?: string;
  disableScroll?: boolean;
}

// Country flag emoji from ISO code
function flagEmoji(code: string): string {
  if (!code || code.length !== 2) return '🌍';
  const offset = 0x1f1e6 - 'A'.charCodeAt(0);
  return String.fromCodePoint(
    code.toUpperCase().charCodeAt(0) + offset,
    code.toUpperCase().charCodeAt(1) + offset,
  );
}

export default function DestinationSearch({
  onSelect,
  value,
  placeholder,
  className = '',
  disableScroll = false,
}: DestinationSearchProps) {
  const { t } = useTranslations();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadPlansAsync().then((plans) => {
      setCountries(listCountries(plans));
      setLoading(false);
    });
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return countries.slice(0, 12);
    return countries.filter((c) => c.name.toLowerCase().startsWith(q)).slice(0, 12);
  }, [query, countries]);

  const handleSelect = (country: Country) => {
    setQuery('');
    setOpen(false);

    // Write country intent for PlanSelector to pick up
    try {
      localStorage.setItem('ps_country_intent', country.code);
      // Also write hero_plan_intent compatible format
      localStorage.setItem(
        'hero_plan_intent',
        JSON.stringify({ mode: 'country', countryCode: country.code }),
      );
      window.dispatchEvent(new Event('flash_plan_intent'));
    } catch {
      // no-op
    }

    // Scroll to plan selector
    if (!disableScroll) {
      const target =
        document.getElementById('pricing');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    onSelect?.(country.code, country.name);
  };

  const selectedCountryName = useMemo(() => {
    if (!value) return '';
    return countries.find((c) => c.code === value)?.name || '';
  }, [value, countries]);

  const ph =
    placeholder || 
    selectedCountryName ||
    t('destinationSearch.placeholder', 'Where are you traveling? (e.g. France)');

  return (
    <div ref={containerRef} className={`relative w-full ${open ? 'z-[100]' : 'z-auto'} ${className}`}>
      <div className="relative flex items-center glass rounded-xl border border-border/60 focus-within:border-primary/60 transition-colors">
        <Search className="absolute left-4 w-4 h-4 text-muted-foreground pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={query ? '' : ph}
          className="w-full bg-transparent pl-11 pr-10 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none"
          aria-label={ph}
          aria-expanded={open}
          aria-haspopup="listbox"
          role="combobox"
          autoComplete="off"
        />
        {query && (
          <button
            className="absolute right-3 p-1 text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => { setQuery(''); inputRef.current?.focus(); }}
            aria-label={t('search.clear', 'Clear search')}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-full z-[1000] bg-white dark:bg-neutral-950 rounded-xl border border-border shadow-[0_20px_50px_rgba(0,0,0,0.5)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.8)] overflow-hidden mt-2 ring-1 ring-black/5 dark:ring-white/10 !opacity-100"
        >
          {loading ? (
            <div className="px-4 py-3 text-sm text-muted-foreground animate-pulse">
              {t('destinationSearch.loading', 'Loading destinations...')}
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-3 text-sm text-muted-foreground">
              {t('destinationSearch.noResults', 'No destination found')}
            </div>
          ) : (
            <ul className="max-h-60 overflow-y-auto py-1">
              {filtered.map((c) => (
                <li key={c.code}>
                  <button
                    role="option"
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-primary/10 transition-colors"
                    onMouseDown={(e) => {
                      e.preventDefault(); // prevent blur before click
                      handleSelect(c);
                    }}
                  >
                    <span className="text-xl leading-none select-none">{flagEmoji(c.code)}</span>
                    <span className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="font-medium">{c.name}</span>
                    </span>
                    <span className="ml-auto text-xs text-muted-foreground uppercase">{c.code}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
